// BL-402 welcome sequence: the pure timing rules, kept apart from the canvas so they can be tested.
// Local timelines map onto the locked website film's finale clock (film seconds 9.5 -> 14.0).
// The logo is "finished" at the end of a plan; if the app isn't ready yet we hold there, breathing.

export const PLANS = {
    full: { from: 9.5, to: 14.0, dur: 3.3, name: true, sheen: true },    // "Welcome, Name…" -> logo -> sheen
    short: { from: 12.0, to: 14.0, dur: 1.2, name: false, sheen: true }, // logo -> sheen only
    fade: { from: 11.8, to: 13.4, dur: 1.0, name: false, sheen: false }, // other tenants: logo fades in, no Welcome text
};
export const EXIT = 0.55;                 // logo fade-out while the Command Center rises in
export const PLAIN_EXIT = 0.3;            // reduced motion / Off: a plain fade
export const HOLD_LINE_DELAY = 0.35;      // the progress line appears only once we've held this long
export const PRELOAD_CAP_MS = 600;        // never wait longer than this for font + logo + context
export const SAFETY_CAP_MS = 15000;       // never trap anyone behind the overlay
export const SETTINGS = ['film_finale', 'fade', 'off'];
export const DEFAULT_SETTING = 'fade';
export const DAY_KEY = 'intelAgentWelcomeDay';

export const clamp = (v, a = 0, b = 1) => Math.max(a, Math.min(b, v));
export const seg = (t, a, b) => clamp((t - a) / (b - a));
export const easeOut = (x) => 1 - Math.pow(1 - x, 3);
export const easeInOut = (x) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);

export function normalizeSetting(value) {
    return SETTINGS.includes(value) ? value : DEFAULT_SETTING;
}

export function localDayKey(date = new Date()) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// Full on the first sign-in of the day on this device, Short after that. Storage trouble means Full.
export function isFirstSignInToday(storage, now = new Date()) {
    const today = localDayKey(now);
    try {
        const seen = storage.getItem(DAY_KEY);
        storage.setItem(DAY_KEY, today);
        return seen !== today;
    } catch (_) {
        return true;
    }
}

// null = no canvas at all (reduced motion, or the tenant turned it Off): a plain fade instead.
export function choosePlan({ setting, reducedMotion, storage, now }) {
    const value = normalizeSetting(setting);
    if (reducedMotion || value === 'off') return null;
    if (value === 'fade') return 'fade';
    return isFirstSignInToday(storage, now) ? 'full' : 'short';
}

// One frame of the sequence. `exitAt` is set the first frame both halves of the exit rule are true:
// (a) the animation reached the finished logo and (b) the app said it is ready.
export function frameAt(state, now, ready) {
    const plan = PLANS[state.plan];
    const elapsed = (now - state.t0) / 1000;
    const local = state.skipped ? plan.dur : Math.min(elapsed, plan.dur);
    const atHold = local >= plan.dur;
    let exitAt = state.exitAt;
    if (!exitAt && ((atHold && ready) || state.aborted)) exitAt = now;
    const heldSince = state.skipped ? state.skippedAt : state.t0 + plan.dur * 1000;
    const hold = atHold && !exitAt ? Math.max(0, (now - Math.max(heldSince, state.t0)) / 1000) : 0;
    const out = exitAt ? easeInOut(clamp((now - exitAt) / (EXIT * 1000))) : 0;
    return {
        filmT: plan.from + (plan.to - plan.from) * (local / plan.dur),
        atHold,
        hold,
        out,
        exitAt,
        done: Boolean(exitAt) && out >= 1,
    };
}

// "Val Whitney" -> "Val". Falls back through auth metadata; '' means show plain "Welcome…".
export function firstNameFrom(context, user) {
    const meta = user?.user_metadata || {};
    const candidates = [context?.first_name, meta.first_name, meta.full_name, meta.name, meta.owner_name];
    for (const candidate of candidates) {
        const first = String(candidate || '').trim().split(/\s+/)[0];
        if (first) return first.slice(0, 24);
    }
    return '';
}

// '1.12.071-beta' -> 'v1.12.071 - Beta'. Always read from src/config/version, never hard-coded.
export function formatVersion(version) {
    const [number, ...stage] = String(version || '').split('-');
    if (!number) return '';
    const label = stage.join('-');
    return label ? `v${number} - ${label.charAt(0).toUpperCase()}${label.slice(1)}` : `v${number}`;
}
